"use client";

import { useState, useMemo } from "react";

export type ExtendedFAQSection = {
  title: string;
  items: { q: string; a: string }[];
};

export function ExtendedFAQSection({
  sections,
  accentColor = "blue",
}: {
  sections: ExtendedFAQSection[];
  accentColor?: "blue" | "indigo" | "orange" | "emerald" | "rose" | "teal";
}) {
  const [search, setSearch] = useState("");
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const accentClasses = {
    blue: { ring: "focus:ring-blue-400", tag: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800", chevron: "text-blue-500", activeSec: "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800", dot: "bg-blue-500" },
    indigo: { ring: "focus:ring-indigo-400", tag: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-300 dark:border-indigo-800", chevron: "text-indigo-500", activeSec: "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800", dot: "bg-indigo-500" },
    orange: { ring: "focus:ring-orange-400", tag: "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800", chevron: "text-orange-500", activeSec: "bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800", dot: "bg-orange-500" },
    emerald: { ring: "focus:ring-emerald-400", tag: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800", chevron: "text-emerald-500", activeSec: "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800", dot: "bg-emerald-500" },
    rose: { ring: "focus:ring-rose-400", tag: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800", chevron: "text-rose-500", activeSec: "bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800", dot: "bg-rose-500" },
    teal: { ring: "focus:ring-teal-400", tag: "bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:text-teal-300 dark:border-teal-800", chevron: "text-teal-500", activeSec: "bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800", dot: "bg-teal-500" },
  }[accentColor];

  const totalCount = useMemo(() => sections.reduce((sum, s) => sum + s.items.length, 0), [sections]);

  const filtered = useMemo(() => {
    if (!search.trim()) return sections;
    const q = search.toLowerCase();
    return sections
      .map(s => ({
        ...s,
        items: s.items.filter(item =>
          item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q)
        ),
      }))
      .filter(s => s.items.length > 0);
  }, [search, sections]);

  const visibleSections = activeSection && !search
    ? filtered.filter(s => s.title === activeSection)
    : filtered;

  const matchCount = filtered.reduce((sum, s) => sum + s.items.length, 0);

  return (
    <div className="mt-16 border-t border-neutral-200 dark:border-neutral-700 pt-12">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-1">
          Complete FAQ Database
        </h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          {totalCount.toLocaleString()} questions covering every aspect of the exam
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder={`Search ${totalCount} questions…`}
          value={search}
          onChange={e => { setSearch(e.target.value); setActiveSection(null); setOpenKey(null); }}
          className={`w-full pl-10 pr-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-xl bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 ${accentClasses.ring} text-sm`}
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
          >
            ✕
          </button>
        )}
      </div>

      {/* Section filter pills */}
      {!search && (
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setActiveSection(null)}
            className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${
              activeSection === null
                ? accentClasses.activeSec
                : "border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:border-neutral-300 dark:hover:border-neutral-600"
            }`}
          >
            All ({totalCount})
          </button>
          {sections.map(s => (
            <button
              key={s.title}
              onClick={() => { setActiveSection(prev => prev === s.title ? null : s.title); setOpenKey(null); }}
              className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${
                activeSection === s.title
                  ? accentClasses.activeSec
                  : "border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:border-neutral-300 dark:hover:border-neutral-600"
              }`}
            >
              {s.title} ({s.items.length})
            </button>
          ))}
        </div>
      )}

      {/* Results count when searching */}
      {search && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
          {matchCount === 0 ? "No results" : `${matchCount} result${matchCount !== 1 ? "s" : ""} for "${search}"`}
        </p>
      )}

      {/* Sections + accordions */}
      <div className="space-y-8">
        {visibleSections.map(section => (
          <div key={section.title}>
            <div className="flex items-center gap-2 mb-3">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${accentClasses.dot}`} />
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">{section.title}</h3>
              <span className="text-xs text-neutral-400">({section.items.length})</span>
            </div>
            <div className="space-y-1.5">
              {section.items.map((item, i) => {
                const key = `${section.title}-${i}`;
                const isOpen = openKey === key;
                return (
                  <div
                    key={key}
                    className="border border-neutral-200 dark:border-neutral-700 rounded-xl overflow-hidden"
                  >
                    <button
                      className="w-full flex items-start justify-between px-4 py-3 text-left bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-750 transition-colors"
                      onClick={() => setOpenKey(isOpen ? null : key)}
                      aria-expanded={isOpen}
                    >
                      <span className="text-sm font-medium text-neutral-800 dark:text-neutral-200 pr-3 leading-snug">
                        {item.q}
                      </span>
                      <svg
                        className={`w-4 h-4 flex-shrink-0 mt-0.5 transition-transform duration-200 ${accentClasses.chevron} ${isOpen ? "rotate-180" : ""}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isOpen && (
                      <div className="px-4 py-3 border-t border-neutral-100 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50">
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{item.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {visibleSections.length === 0 && (
          <div className="text-center py-12 text-neutral-400">
            <svg className="w-10 h-10 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm">No questions match your search.</p>
            <button onClick={() => setSearch("")} className="text-sm text-blue-500 mt-1 hover:underline">Clear search</button>
          </div>
        )}
      </div>
    </div>
  );
}
